import { FC, memo, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { dom } from 'app/utils';
import { Icon } from 'app/components/common';
import { TextBox, Select, Switch } from 'app/components/form';
import { Display } from './display';
import { DataTable as Component, DataTableProps as Props, Column as Subcomponent, ColumnProps as Subprops } from '../external';
import { IComponent, ISource } from '../types';
import { IFilter, IFilterer, IHeader, IPaginator, IPaging, ISort } from './types';

export interface ICell {
  field: string;
  fixed?: boolean;
  index: number;
  props?: any;
  source?: ISource;
  emptyText?: string;
}

export interface IColumn<T> extends IComponent {
  align?: 'left' | 'center' | 'right';
  columnKey?: string;
  field?: string;
  type?: 'bool' | 'boolean' | 'date' | 'datetime' | 'decimal' | 'double' | 'guid' | 'int' | 'number' | 'string' | 'time';
  filter?: boolean | IFilterer;
  header?: string | IHeader;
  reorderable?: boolean;
  resizeable?: boolean;
  sortable?: boolean;
  fixed?: boolean;
  visible?: boolean;
  template?: (item: T, column: ICell) => ReactNode;
  source?: ISource;
  emptyText?: string;
}

export interface ITable<T extends Record<string, any>> extends IComponent {
  columnResizeMode?: 'fit' | 'expand';
  columns?: IColumn<T>[];
  headers?: boolean;
  data?: T[];
  dataKey?: string;
  loading?: boolean,
  paginator?: IPaginator;
  sort?: ISort,
  filters?: Record<string, any>;
  removableSort?: boolean;
  resizableColumns?: boolean;
  reorderableColumns?: boolean;
  scrollable?: boolean;
  scrollHeight?: string;
  selection?: T[];
  selectionMode?: 'single' | 'multiple';
  checkbox?: boolean;
  expandable?: boolean;
  stateKey?: string;
  stripedRows?: boolean;
  total?: number;

  template?: (item: T) => ReactNode;

  onLoad?: (e: IPaging) => void;

  onRowClick?: (e: T) => void;
  onRowDoubleClick?: (e: T) => void;

  onRowToggle?: (e: T) => void;
  onSelectionChange?: (e: T[]) => void;
}

const mappings = new Map<string, string>();
mappings.set('bool', 'boolean');
mappings.set('datetime', 'date');
mappings.set('decimal', 'numeric');
mappings.set('double', 'numeric');
mappings.set('guid', 'string');
mappings.set('int', 'numeric');
mappings.set('number', 'numeric');
mappings.set('string', 'text');
mappings.set('time', 'date');

const orders = new Map<number, string>();
orders.set(1, 'asc');
orders.set(-1, 'desc');

const sorts = new Map<string, number>();
sorts.set('asc', 1);
sorts.set('desc', -1);

const templates = new Map<string, (item: any, column: ICell) => ReactNode>();
templates.set('boolean', (item, column) => { const value = item[column.field]; return (<Icon name={value ? 'mdi-check-circle' : 'mdi-circle'} className={['block', (value ? 'text-green-500' : 'text-gray-300')]} />); });
templates.set('date', (item, column) => { const value = item[column.field]; return (value && DateTime.fromISO(value).toFormat('D')); });
templates.set('datetime', (item, column) => { const value = item[column.field]; return (value && DateTime.fromISO(value).toFormat('F')); });
templates.set('source', (item, column) => (<Display value={item[column.field]} source={column.source} emptyText={column.emptyText} />));

const filterers = new Map<string, (options: IFilter) => ReactNode>();
filterers.set('boolean', ({ value, change }) => (<Switch checked={value} onChange={dom.handle(change, ({ target }) => target.value)} />));
filterers.set('string', ({ value, change }) => (<TextBox value={value} onChange={dom.handle(change, ({ target }) => target.value)} />));
filterers.set('source', ({ value, source, change }) => (<Select source={source} value={value} onChange={dom.handle(change, ({ target }) => target.value)} />));

const paginators = new Map<string, string>();
paginators.set('first', 'FirstPageLink');
paginators.set('prev', 'PrevPageLink');
paginators.set('page', 'CurrentPageReport');
paginators.set('pages', 'PageLinks');
paginators.set('next', 'NextPageLink');
paginators.set('last', 'LastPageLink');
paginators.set('rows', 'RowsPerPageDropdown');

const pagination = (template: IPaginator['template']) => template?.map((template) => paginators.get(template)).join(' ');

export const Table: FC<ITable<any>> = memo(({ id, className, style, disabled, columnResizeMode, columns, headers, data, dataKey, loading, paginator, sort, filters, removableSort, resizableColumns, reorderableColumns, scrollable, scrollHeight, selection, selectionMode, checkbox, expandable, stateKey, stripedRows, total, template, onLoad, onRowClick, onRowDoubleClick, onRowToggle, onSelectionChange }) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<any>();

  const handler = dom.handle(onLoad, ({ page, pageCount, rows, sortField, sortOrder, filters }) => ({ rows, filters,
    page: (page ?? 0),
    count: pageCount,
    sort: { field: sortField, order: orders.get(sortOrder) },
  }));

  filters ??= {};
  const children = columns?.map<Subprops>(({ className, style, align, columnKey, field, type, filter, header, reorderable, resizeable, sortable, fixed, visible, template, source, emptyText }) => {
    if (typeof header === 'string') header = { label: header };
    if (typeof filter === 'boolean') filter = (filter ? { field } : undefined);

    if (filter) filters[field!] ??= { value: undefined, matchMode: filter?.matchMode };
    const dataType = (mappings.get(type!) ?? type);

    visible ??= true;
    template ??= (source ? templates.get('source') : (templates.get(type!) ?? templates.get(dataType!)));

    return { style, dataType, align, columnKey, field, reorderable, resizeable, sortable,
      className: dom.className('i-column', className),
      header: header?.label,
      alignHeader: header?.align,
      headerClassName: dom.className('i-header', header?.className),
      headerStyle: header?.style,
      frozen: fixed,
      hidden: !visible,

      body: dom.handle(template, (item, { field, frozenRow, rowIndex, props }) => [item, { field, props, source,
        fixed: frozenRow,
        index: rowIndex,
      }]),

      filter: !!filter,
      filterField: (filter?.field ?? field),
      filterMatchMode: (filters[field!]?.matchMode ?? filter?.matchMode),
      filterElement: dom.handle(filter?.template ?? filterers.get(source ? 'source' : dataType!), ({ field, index, value, filterCallback, filterApplyCallback }) => ({ field, index, value, header, source, emptyText,
        change: (value: any) => filterCallback(value, index),
        apply: (value: any) => filterApplyCallback(value, index),
      })),
      filterMenuStyle: filter?.style,
      filterMenuClassName: dom.className(filter?.className),
      showFilterMatchModes: !source, // !filter?.matchMode,
    } as Subprops;
  });

  const table: Props<any[]> = { id, style, disabled, columnResizeMode, dataKey, loading, filters, removableSort, resizableColumns, reorderableColumns, scrollable, scrollHeight, stateKey, stripedRows, expandedRows,
    className: dom.className('i-component', 'i-table', className),
    showHeaders: headers,
    lazy: !!onLoad,
    metaKeySelection: (selectionMode === 'multiple'),
    paginator: !!paginator,
    paginatorClassName: dom.className(paginator?.className),
    paginatorPosition: (paginator?.position ?? 'bottom'),
    paginatorTemplate: pagination(paginator?.template ?? ['page', 'first', 'prev', 'pages', 'next', 'last', 'rows']),
    currentPageReportTemplate: t('app.table.page'),
    filterDisplay: 'menu',
    // filterIcon: () => undefined,
    sortField: sort?.field,
    sortOrder: sorts.get(sort?.order ?? '') as any,
    // sortIcon: ({ sorted, sortOrder }) => undefined,
    alwaysShowPaginator: paginator?.show,
    rows: (paginator?.rows ?? paginator?.options?.[0]),
    rowsPerPageOptions: paginator?.options,
    selection: selection,
    selectionMode: (checkbox ? (selectionMode === 'multiple' ? 'checkbox' : 'radiobutton') : selectionMode) as any,
    stateStorage: (stateKey ? 'local' : undefined),
    value: data,
    totalRecords: (total ?? data?.length),
    emptyMessage: t('app.table.empty'),
    first: (paginator?.page ?? 0) * (paginator?.rows ?? paginator?.options?.[0] ?? 0),

    onFilter: handler,
    onPage: handler,
    onSort: handler,

    rowExpansionTemplate: template,

    onRowClick: dom.handle(onRowClick, ({ data }) => data),
    onRowDoubleClick: dom.handle(onRowDoubleClick, ({ data }) => data),

    onRowToggle: (e) => setExpandedRows(e.data),

    onRowExpand: dom.handle(onRowToggle, ({ data }) => ({ item: data, expanded: true })),
    onRowCollapse: dom.handle(onRowToggle, ({ data }) => ({ item: data, expanded: false })),

    onSelectionChange: dom.handle(onSelectionChange,  ({ value }) => value),
  };

  return (
    <Component { ...table }>
      { checkbox && <Subcomponent selectionMode={selectionMode} headerClassName="w-3rem" /> }
      { expandable && <Subcomponent expander headerClassName="w-3rem" /> }
      { children?.map((column) => (
        <Subcomponent key={column.field} { ...column } />
      )) }
    </Component>
  );
});
